import { useMemo, useState } from "react";

import {
	ImageBackground,
	type ImageContentFit,
	type ImageStyle,
} from "expo-image";
import { TouchableOpacity } from "react-native";

import { isWeb } from "@memorang/helpers/platform";
import { useImageDimensions } from "@react-native-community/hooks";
import { useAppTheme } from "../../useAppTheme";

import { Text } from "react-native-paper";
import { AppIcon } from "../AppIcon";
import Box from "../Box";
import DisableContextMenu from "../DisableContextMenu";
import ImageViewerModal from "../ImageViewerModal";
import { getSingleImageRenderSize } from "../ImagesCarousel/helpers";
import { ImageSkeleton } from "../Skeletons/ImageSkeleton";

type BaseProps = {
	url: string;
	name?: string;
	allImages: { url: string; caption?: string }[];
	borderRadius?: number;
	initialIndex?: number;
	contentFit?: ImageContentFit;
};

type FixedSizeProps = (ImageStyle | undefined) & {
	width?: number;
	height?: number;
};

type AutoSizeProps = {
	/** Enable intrinsic-size driven layout (used for single image) */
	autoSize?: boolean;
	/** Container width to fill (acts like maxWidth: 100%) */
	containerWidth?: number;
	/** Clamp height for tall images (e.g., 200–360) */
	maxHeight?: number;
	/** Minimum height (autoSize only); image won't shrink below this. */
	minHeight?: number;
	/** Show a magnify badge hinting the image can be tapped to zoom */
	showZoomIndicator?: boolean;
};

type Props = BaseProps & FixedSizeProps & AutoSizeProps;

function useImageRenderSize(params: {
	url: string;
	autoSize?: boolean;
	containerWidth?: number;
	maxHeight?: number;
	minHeight?: number;
	width?: number;
	height?: number;
}) {
	const { url, autoSize, containerWidth, maxHeight, minHeight, width, height } =
		params;

	const { dimensions, loading } = useImageDimensions({
		uri: url,
	});

	const isFixed =
		!autoSize && typeof width === "number" && typeof height === "number";

	const { displayWidth, displayHeight } = useMemo(() => {
		if (isFixed) {
			return { displayWidth: width as number, displayHeight: height as number };
		}

		const aspect =
			dimensions?.width && dimensions?.height
				? dimensions.width / dimensions.height
				: 1;

		const baseWidth =
			autoSize && containerWidth ? containerWidth : (width ?? 200);
		const renderSize = getSingleImageRenderSize({
			containerWidth: baseWidth,
			aspectRatio: aspect,
			maxHeight,
			minHeight,
		});

		return { displayWidth: renderSize.width, displayHeight: renderSize.height };
	}, [
		isFixed,
		width,
		height,
		autoSize,
		containerWidth,
		maxHeight,
		minHeight,
		dimensions?.width,
		dimensions?.height,
	]);

	const showLoader = Boolean(autoSize && loading);

	return { displayWidth, displayHeight, showLoader };
}

export const ZoomImage = ({
	url,
	borderRadius,
	name,
	allImages,
	initialIndex,
	contentFit = "cover",

	width,
	height,
	autoSize,
	containerWidth,
	maxHeight,
	minHeight,
	showZoomIndicator,

	...imageStyle
}: Props) => {
	const [showFullScreenImage, setShowFullScreenImage] = useState(false);
	const theme = useAppTheme();

	const { displayWidth, displayHeight, showLoader } = useImageRenderSize({
		url,
		autoSize,
		containerWidth,
		maxHeight,
		minHeight,
		width,
		height,
	});

	const toggleFullScreenImage = () => setShowFullScreenImage((p) => !p);

	return (
		<DisableContextMenu>
			<TouchableOpacity
				onLongPress={toggleFullScreenImage}
				onPress={toggleFullScreenImage}
				delayPressIn={150}
			>
				{url ? (
					showLoader ? (
						<ImageSkeleton
							width={displayWidth}
							height={displayHeight}
							borderRadius={borderRadius}
						/>
					) : (
						<ImageBackground
							style={
								{
									width: displayWidth,
									height: displayHeight,
									marginTop: 8,
									position: "relative",
									justifyContent: "flex-end",
									borderRadius: borderRadius || theme.roundness,
									borderWidth: 1,
									borderColor: theme.colors.outlineVariant,
									alignItems: "center",
									alignSelf: "center",
									...(imageStyle || {}),
									...(isWeb ? { cursor: "zoom-in" } : {}),
								} as ImageStyle
							}
							imageStyle={{
								borderRadius: borderRadius || theme.roundness,
							}}
							contentFit={contentFit}
							contentPosition="center"
							cachePolicy="memory-disk"
							source={{ uri: url }}
						>
							{showZoomIndicator ? (
								<Box
									position="absolute"
									top={8}
									right={8}
									padding={6}
									borderRadius={999}
									borderWidth={1}
									borderColor={theme.colors.outlineVariant}
									backgroundColor={theme.colors.surface}
									elevation={2}
									style={{
										shadowColor: theme.colors.shadow,
										shadowOpacity: 0.2,
										shadowRadius: 4,
										shadowOffset: { width: 0, height: 1 },
									}}
								>
									<AppIcon
										name="magnify-plus-outline"
										size={18}
										color={theme.colors.primary}
									/>
								</Box>
							) : null}
							{name && (
								<Text
									variant="labelMedium"
									style={{
										color: "#fff",
										fontFamily: "OpenSans-SemiBold",
										paddingBottom: 8,
										textAlign: "center",
									}}
								>
									{name}
								</Text>
							)}
						</ImageBackground>
					)
				) : null}

				<ImageViewerModal
					visible={showFullScreenImage}
					images={allImages}
					initialIndex={initialIndex}
					handleClose={() => setShowFullScreenImage(false)}
				/>
			</TouchableOpacity>
		</DisableContextMenu>
	);
};
